import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  CalendarDays,
  Lock,
  PowerOff,
  Pencil,
  List,
  UserX,
  ImageIcon,
  Gauge,
  Settings,
  Share2,
  SlidersHorizontal,
  Sparkles,
  ScrollText,
  Backpack,
  Dumbbell,
  MapPin,
  Clock,
  Users,
  UserCog,
  UserPlus,
  GraduationCap,
  CalendarClock,
  Trophy,
} from "lucide-react";

const SCREENSHOTS = import.meta.glob<{ default: string }>("./*.png", {
  eager: true,
});

function screenshotUrl(id: string): string | undefined {
  return SCREENSHOTS[`./${id}.png`]?.default;
}
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@sportscanner/ui";
import { hasFeature } from "@sportscanner/schemas";
import { useMe, type MeClub } from "@/lib/me";

type SectionId =
  | "dashboard"
  | "bookASlot"
  | "blockSlot"
  | "closeDay"
  | "manageBooking"
  | "listAndSearch"
  | "noShow"
  | "settings"
  | "access"
  | "limits"
  | "amenities"
  | "gear"
  | "clubRules"
  | "integration"
  | "sports"
  | "courts"
  | "coaches"
  | "coaching"
  | "competitions"
  | "timeSlots"
  | "members"
  | "admins"
  | "invite";

type Role = "STAFF" | "MANAGER" | "SUPERADMIN" | "COACH";

interface VisibilityContext {
  role: Role;
  club: MeClub | null;
}

interface Section {
  id: SectionId;
  icon: React.ComponentType<{ className?: string }>;
  itemIds: string[];
  hasNote?: boolean;
  // "dialog" caps the screenshot at roughly the real dialog width on desktop
  // so it doesn't fill the whole help column. "page" (default) lets the image
  // span the column for full-page screens.
  screenshotKind?: "dialog" | "page";
  // Defaults to visible for every role when omitted.
  // Permission-gating within a role (e.g. hiding blockSlot from STAFF lacking
  // manage_blocks) is intentionally deferred — keep it role-coarse for now.
  visibleTo?: (ctx: VisibilityContext) => boolean;
}

const isManager = ({ role }: VisibilityContext) =>
  role === "MANAGER" || role === "SUPERADMIN";

const SECTIONS: Section[] = [
  // ─── Coach-only — the coach's own schedule screen ───────────────────────
  {
    id: "coaching",
    icon: CalendarClock,
    itemIds: ["sports", "availability", "timeOff", "sessions"],
    visibleTo: ({ role }) => role === "COACH",
  },

  // ─── Operational — visible to every role ────────────────────────────────
  {
    id: "dashboard",
    icon: LayoutDashboard,
    itemIds: [
      "quickActions",
      "today",
      "thisWeek",
      "occupancy",
      "revenue",
      "upcoming",
      "courtStatus",
      "weeklyRevenue",
      "popularTimes",
      "coaches",
    ],
  },
  {
    id: "bookASlot",
    icon: CalendarDays,
    itemIds: [
      "fromButton",
      "fromCalendar",
      "pickSlot",
      "coach",
      "summary",
      "roster",
      "recurring",
      "conflict",
    ],
    hasNote: true,
    screenshotKind: "dialog",
  },
  {
    id: "blockSlot",
    icon: Lock,
    itemIds: [
      "openDialog",
      "pickCourtsAndTime",
      "reason",
      "recurring",
      "remove",
    ],
    screenshotKind: "dialog",
  },
  {
    id: "closeDay",
    icon: PowerOff,
    itemIds: ["openDialog", "pickDates", "reason", "remove"],
    hasNote: true,
    screenshotKind: "dialog",
  },
  {
    id: "manageBooking",
    icon: Pencil,
    itemIds: ["open", "cancel", "reschedule", "cancelSeries"],
    hasNote: true,
    screenshotKind: "dialog",
  },
  {
    id: "listAndSearch",
    icon: List,
    itemIds: [
      "switchView",
      "searchName",
      "searchPhone",
      "filterCourt",
      "range",
      "pagination",
    ],
  },
  {
    id: "noShow",
    icon: UserX,
    itemIds: ["flag", "badge", "filter", "unflag"],
    screenshotKind: "dialog",
  },

  // ─── Configuration — manager+ only ──────────────────────────────────────
  {
    id: "settings",
    icon: Settings,
    itemIds: [
      "basic",
      "more",
      "workingHours",
      "bookingDays",
      "description",
      "isFree",
      "save",
    ],
    visibleTo: isManager,
  },
  {
    id: "access",
    icon: SlidersHorizontal,
    itemIds: ["account", "contact", "reminders", "coaching", "save"],
    hasNote: true,
    visibleTo: isManager,
  },
  {
    id: "limits",
    icon: Gauge,
    itemIds: ["caps", "save"],
    visibleTo: isManager,
  },
  {
    id: "amenities",
    icon: Sparkles,
    itemIds: ["pick", "save", "publicDisplay"],
    visibleTo: isManager,
  },
  {
    id: "gear",
    icon: Backpack,
    itemIds: ["enable", "add", "stock", "booking"],
    hasNote: true,
    visibleTo: isManager,
  },
  {
    id: "clubRules",
    icon: ScrollText,
    itemIds: ["write", "languages", "publicDisplay", "save"],
    visibleTo: isManager,
  },
  {
    id: "integration",
    icon: Share2,
    itemIds: ["publicLink", "qr", "iframe", "embedMode", "webComponent"],
    hasNote: true,
    visibleTo: isManager,
  },
  {
    id: "sports",
    icon: Dumbbell,
    itemIds: ["add", "duration", "pending", "deleteBlocked", "save"],
    visibleTo: isManager,
  },
  {
    id: "courts",
    icon: MapPin,
    itemIds: ["add", "edit", "reorder", "hide", "delete"],
    visibleTo: isManager,
    hasNote: true,
  },
  {
    id: "coaches",
    icon: GraduationCap,
    itemIds: ["invite", "sportsPrices", "activate", "profile"],
    hasNote: true,
    // Mirrors the sidebar: managers see this when their tier includes coaching;
    // superadmin always sees the docs (they manage coaches via the club picker).
    visibleTo: ({ role, club }) =>
      role === "SUPERADMIN" ||
      (role === "MANAGER" &&
        !!club &&
        hasFeature(club.subscriptionTier, "allowCoaching")),
  },
  {
    id: "competitions",
    icon: Trophy,
    itemIds: ["enable", "create", "registrations", "structure", "results"],
    hasNote: true,
    // Mirrors the sidebar: managers see this when their tier includes
    // competitions; superadmin always sees the docs.
    visibleTo: ({ role, club }) =>
      role === "SUPERADMIN" ||
      (role === "MANAGER" &&
        !!club &&
        hasFeature(club.subscriptionTier, "competitionsEnabled")),
  },
  {
    id: "timeSlots",
    icon: Clock,
    itemIds: [
      "overview",
      "periods",
      "pricingSchedule",
      "memberOnly",
      "prime",
      "maintenance",
      "reset",
      "save",
    ],
    hasNote: true,
    // Available to every MANAGER and SUPERADMIN. The page itself surfaces
    // member-only / prime checkboxes only when the corresponding club flag
    // is on; duration, price, and maintenance are tier-agnostic.
    visibleTo: ({ role }) => role === "SUPERADMIN" || role === "MANAGER",
  },
  {
    id: "members",
    icon: Users,
    itemIds: ["tabs", "search", "approve", "reject", "revoke"],
    hasNote: true,
    // Mirrors sidebar: members page only makes sense when the club actually
    // has a membership concept.
    visibleTo: ({ role, club }) =>
      role === "SUPERADMIN" ||
      (role === "MANAGER" &&
        Boolean(club?.requireMembership || club?.allowMemberOnlySlots)),
  },
  {
    id: "admins",
    icon: UserCog,
    itemIds: ["edit", "permissions", "passwordReset", "delete", "selfHint"],
    visibleTo: isManager,
  },
  {
    id: "invite",
    icon: UserPlus,
    itemIds: ["staffTab", "managerTab", "permissions", "send", "accept"],
    visibleTo: isManager,
  },
];

function Screenshot({
  id,
  kind,
}: {
  id: SectionId;
  kind: Section["screenshotKind"];
}) {
  const { t } = useTranslation();
  const url = screenshotUrl(id);
  // Dialog screenshots get capped to roughly the real dialog width so they
  // don't blow up to the full help column on desktop. Page screenshots fill
  // the column. Mobile stays full-width either way.
  const widthClass = kind === "dialog" ? "md:max-w-lg md:mx-auto" : "";

  if (url) {
    return (
      <div
        className={`mt-4 border border-neutral-200 rounded-lg overflow-hidden bg-neutral-50 ${widthClass}`}
      >
        <img
          src={url}
          alt={t(`help.sections.${id}.title`)}
          className="w-full h-auto block"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className={`mt-4 border-2 border-dashed border-neutral-200 rounded-lg bg-neutral-50 p-6 flex flex-col items-center justify-center gap-2 text-neutral-400 ${widthClass}`}
    >
      <ImageIcon className="w-6 h-6" />
      <p className="text-xs text-center">
        {t("help.screenshotPlaceholder", {
          file: `help/${id}.png`,
        })}
      </p>
    </div>
  );
}

function HelpItem({
  sectionId,
  itemId,
}: {
  sectionId: SectionId;
  itemId: string;
}) {
  const { t } = useTranslation();
  return (
    <li className="text-sm text-neutral-700 leading-relaxed">
      <span className="font-semibold text-neutral-800">
        {t(`help.sections.${sectionId}.items.${itemId}.title`)}
      </span>{" "}
      <span>{t(`help.sections.${sectionId}.items.${itemId}.body`)}</span>
    </li>
  );
}

function HelpSection({ section }: { section: Section }) {
  const { t } = useTranslation();
  const Icon = section.icon;
  return (
    <AccordionItem
      value={section.id}
      className="border border-neutral-200 rounded-xl bg-white overflow-hidden"
    >
      <AccordionTrigger className="px-4 sm:px-5 py-4 hover:no-underline">
        <div className="flex items-center gap-3 text-left">
          <span className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4" />
          </span>
          <span className="font-heading font-semibold text-neutral-800">
            {t(`help.sections.${section.id}.title`)}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 sm:px-5 pb-5 space-y-3">
        <p className="text-sm text-neutral-600 leading-relaxed">
          {t(`help.sections.${section.id}.intro`)}
        </p>
        <ul className="space-y-2 list-disc pl-5 marker:text-neutral-300">
          {section.itemIds.map((itemId) => (
            <HelpItem key={itemId} sectionId={section.id} itemId={itemId} />
          ))}
        </ul>
        {section.hasNote && (
          <p className="text-sm text-neutral-500 italic leading-relaxed">
            {t(`help.sections.${section.id}.note`)}
          </p>
        )}
        <Screenshot id={section.id} kind={section.screenshotKind} />
      </AccordionContent>
    </AccordionItem>
  );
}

export default function HelpPage() {
  const { t } = useTranslation();
  const { user, club } = useMe();
  const role: Role = user?.role ?? "STAFF";

  // A coach only has the schedule page (plus account + this help), so they see
  // only the coaching section — never the operational/config docs for pages they
  // can't open.
  const visible =
    role === "COACH"
      ? SECTIONS.filter((s) => s.id === "coaching")
      : SECTIONS.filter((s) => !s.visibleTo || s.visibleTo({ role, club }));

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-4xl mx-auto">
      <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800 mb-1">
        {t("help.title")}
      </h1>
      <p className="text-sm text-neutral-500 mb-6">{t("help.subtitle")}</p>

      <Accordion
        type="multiple"
        defaultValue={visible.length > 0 ? [visible[0].id] : []}
        className="space-y-3"
      >
        {visible.map((section) => (
          <HelpSection key={section.id} section={section} />
        ))}
      </Accordion>
    </div>
  );
}
